import React, { useEffect, useState } from 'react';
import Modal from '../../../../../components/Modal';
import deleteMenu from '../../../../../assets/images/deleteMenu.png';
import PropTypes from 'prop-types';
import { useAssignmentContext } from '../../../context';
import CustomToast from '../../../../../components/Toast';
import { useDeleteAssignmentsMutation } from '../../../../../features/authentication/assignApiSlice';
import { useDeleteFlashcardsMutation } from '../../../../../features/authentication/activityApiSlice';
import { useActivityContext } from '../../../../activities/context';
import ActivityTypes from '../../../../../enums/activityTypes';

const DeleteMenuModal = ({ modalData, setModalData, fromMoreActions, activityType }) => {
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const [deleteAssignments, { isLoading: isDeleting }] = useDeleteAssignmentsMutation();
  const [deleteFlashcards, { isLoading: isDeletingFlashcards }] = useDeleteFlashcardsMutation();

  const assignmentContext = useAssignmentContext();
  const activityContext = useActivityContext();

  const isFlashCard = activityType === ActivityTypes.FlashCard;
  const context = isFlashCard ? activityContext : assignmentContext;
  const isLoading = isDeleting || isDeletingFlashcards;

  const closeErrorHandler = () => {
    context.setAssignedDataStatus(new Date().getSeconds());
    context.setAssignedData([]);
    setModalData(false);
  };

  const setDeleteOption = () => {
    const allSelected = context.isAllSelected;

    return {
      skip: 0,
      take: 0,
      courseId: context.filters.courseId,
      folderId: context.filters.folderId,
      sortColumn: isFlashCard ? context.sortColumn ?? null : context.filters.sortColumn ?? null,
      sortDirection: isFlashCard ? context.sortDirection ?? null : context.filters.sortDirection ?? null,
      assignmentStatus: isFlashCard ? context.filters.activityStatus : context.filters.assignmentStatus,
      ...(isFlashCard ? {} : { assignedVia: context.selectedAssignedViaId?.map(Number) ?? [] }),
      isCollapsed: Boolean(context.filters.isCollapsed),
      searchText: context.filters.selectedKeyWord,
      assignmentIds: fromMoreActions
        ? [context.activityId || context.assignmentId]
        : allSelected ? [-1] : context.assignedData.map((item) => item.assignmentId),
      assignedVia: isFlashCard ? [] : context.filters.assignedVia
    };
  };

  const deleteAsync = async () => {
    if (isLoading) return;
    try {
      const option = setDeleteOption();
      const mutationFn = isFlashCard ? deleteFlashcards : deleteAssignments;

      await mutationFn(option).unwrap();
      CustomToast(`${isFlashCard ? 'Flashcard' : 'Assignment'}(s) Deleted Successfully`, 'success');

      context.setAssignedDataStatus(new Date().getSeconds());
      context.setAssignedData([]);
      context.setAssignedCount(0);
      setShowErrorMsg(false);
      setModalData(false);
    } catch (ex) {
      console.log(ex);
    }
  };

  useEffect(() => {
    const notAssignedCount = context.assignedData.length > 0
      ? context.assignedData.filter(x => x.assignedCount > 0).length
      : context.assignedCount;

    setShowErrorMsg(notAssignedCount > 0);
  }, [context.assignedData, context.assignedCount, activityType]);

  const activityLabel = isFlashCard ? 'flashcard' : 'assignment';

  const getConfirmationText = ()=>{
    if (context.isAllSelected)
      return `Are you sure that you want to delete <strong>all ${activityLabel}(s)</strong>?`;

    if (context.assignedData.length > 1)
      return `Are you sure that you want to delete checked <strong>${context.assignedData.length} ${activityLabel}(s)</strong>?`;

    return `Are you sure that you want to delete this ${activityLabel}?`;
  };

  return (
    <Modal
      isOpen={modalData}
      onClose={() => setModalData(false)}
      contentWidth="700px"
      titleName={`${showErrorMsg ? 'Error' : `Delete ${isFlashCard ? 'Flashcard' : 'Assignment'}`}`}
    >
      {!showErrorMsg && (
        <div className="confirmation-wrapper m-3 pt-3">
          <div className="confirmation-content p-3">
            <div className="confirmation-icon pb-3">
              <img src={deleteMenu} className="img-fluid" alt="Delete" />
            </div>
            <div>
              <p className="mb-0" dangerouslySetInnerHTML={{ __html: getConfirmationText() }}></p>
            </div>
          </div>
          <div className="d-flex justify-content-center gap-2 delete-btn">
            <button className="btn btn-castleWhite" onClick={() => setModalData(false)} disabled={isLoading}>
              No
            </button>
            <button className="btn btn-castleRed" onClick={deleteAsync} disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Deleting...
                </>
              ) : (
                'Yes'
              )}
            </button>
          </div>
        </div>
      )}
      {showErrorMsg && (
        <div className="confirmation-wrapper">
          <div className="confirmation-content">
            <div>
              <p className="mb-0">
                You have not selected any unassigned {activityLabel}(s) to delete. Please unassign all current and removed students from the selected {activityLabel}(s). Check &quot;Display Removed Students&quot; on the {isFlashCard ? 'Flashcard' : 'Assignment'} Details page.
              </p>
            </div>
          </div>
          <div className="d-flex justify-content-center gap-2 delete-btn">
            <button className="btn btn-castleRed" onClick={closeErrorHandler}>
              Ok
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

DeleteMenuModal.propTypes = {
  modalData: PropTypes.bool,
  setModalData: PropTypes.func,
  fromMoreActions: PropTypes.bool,
  activityType: PropTypes.number
};

export default DeleteMenuModal;
